import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE } from "@/lib/api";
import { Sparkles, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "nv_admin_jwt";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) { setLoadError("Missing invite token."); setLoadingInfo(false); return; }
      try {
        const r = await fetch(`${API_BASE}/auth/invite-info?token=${encodeURIComponent(token)}`);
        const data = await r.json();
        if (!r.ok) {
          setLoadError(typeof data.detail === "string" ? data.detail : "Invalid invitation.");
        } else {
          setInfo(data);
        }
      } catch (e) {
        setLoadError(String(e.message || e));
      } finally { setLoadingInfo(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) { setError("Your name is required."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name: name.trim(), password }),
      });
      const data = await r.json();
      if (!r.ok) { setError(typeof data.detail === "string" ? data.detail : "Couldn't accept invite."); return; }
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success(`Welcome — you&apos;re in ${data.workspace?.business_name || "the workspace"}`);
      setTimeout(() => nav("/studio"), 800);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#183D36] mb-6">
          <ArrowLeft size={14} /> Home
        </Link>
        <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
              <Sparkles size={16} />
            </span>
            <h1 className="font-heading text-2xl text-[#183D36]">Accept your invitation</h1>
          </div>
          {loadingInfo ? (
            <p className="text-sm text-[#4A4A4A]">Loading invite…</p>
          ) : loadError ? (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-3" data-testid="invite-error">
              <AlertCircle size={14} className="mt-0.5 flex-none" />
              <span>{loadError}</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#4A4A4A] mb-6">
                {info?.invited_by_name ? <b>{info.invited_by_name}</b> : "Someone"} invited you to join <b className="text-[#183D36]">{info?.workspace_name}</b> on NextVisit as a <b>{info?.role || "member"}</b>. Set a password to get started.
              </p>
              <form onSubmit={submit} className="grid gap-4" data-testid="invite-form">
                <div className="grid gap-2">
                  <Label>Email</Label>
                  <Input value={info?.email || ""} disabled data-testid="invite-input-email" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="inv-name">Your name</Label>
                  <Input id="inv-name" data-testid="invite-input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="inv-pw">Choose a password</Label>
                  <Input id="inv-pw" data-testid="invite-input-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" required />
                </div>
                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-3" data-testid="invite-submit-error">
                    <AlertCircle size={14} className="mt-0.5 flex-none" />
                    <span>{error}</span>
                  </div>
                )}
                <Button type="submit" disabled={busy} data-testid="invite-submit" className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]">
                  {busy ? "Joining…" : (
                    <>
                      <CheckCircle2 size={14} className="mr-1" /> Join workspace
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
